/**
 * <ts-quick-view> — singleton controller for the shared Quick View modal.
 *
 * Modeled on `assets/ts-look-card.js`: fetch the product page (the TS PDP is
 * the default product template), extract `.product-information`, adopt the
 * fetched page's assets, and inject the markup into a centered
 * `<dialog-component>`. Injecting the real PDP top section means the brand
 * link, price/sale, badges, variant picker, ATC, gift-card form, reviews, and
 * fit slider all come from the PDP — no bespoke re-implementation.
 *
 * The modal snippet may be rendered by many product cards on one page, so the
 * FIRST connected instance becomes THE controller: it relocates its dialog to
 * <main>, owns a single document-level capture-phase listener for
 * `.ts-quick-view-trigger`, and every other instance removes its own markup and
 * defers. The trigger's product URL is resolved from the nearest card's link at
 * click time, so triggers stay data-free and identical across card types.
 *
 * Click flow: resolve the card's product URL, show a loading overlay, fetch the
 * product page, open the (empty) dialog so the gallery slideshow measures a real
 * viewport, inject `.product-information`, hide the overlay.
 */

import { watchThirdPartyModals } from '@ts/modal-overlay-guard';
import { initKiwiSizeGuide, isKiwiHydrated } from '@ts/kiwi-hydration';

/** @typedef {HTMLElement & { showDialog?: () => void }} QuickViewDialogElement */

const PDP_VIEW = 'ts-pdp-standard';

/**
 * Module-level singleton. This file loads as `type="module"`, so it executes
 * once per page even when several cards each render the modal snippet — the
 * first `<ts-quick-view>` to connect claims control; the rest remove themselves.
 * @type {TsQuickView | null}
 */
let activeController = null;

class TsQuickView extends HTMLElement {
  /** @type {HTMLElement | null} */
  #content = null;
  /** @type {HTMLElement | null} */
  #dialog = null;
  /** @type {QuickViewDialogElement | null} */
  #dialogComponent = null;
  /** @type {HTMLElement | null} */
  #loadingOverlay = null;
  /**
   * Pristine `.product-information` snapshot per product URL. Never inserted
   * itself — opening a product that isn't already mounted injects a fresh
   * clone (see #open).
   * @type {Map<string, Element>}
   */
  #sections = new Map();
  /** @type {string} */
  #currentUrl = '';
  /** @type {string} Product URL of the section currently mounted in the dialog. */
  #injectedUrl = '';
  /** @type {boolean} */
  #loading = false;
  /** @type {HTMLElement | null} The trigger that opened the current modal. */
  #lastTrigger = null;
  /** @type {boolean} Whether the current open was a pointer click (vs keyboard). */
  #openedByPointer = false;

  connectedCallback() {
    // Singleton: the first instance wins and owns the shared dialog. Extra
    // instances (one per card that renders the modal snippet) drop their markup.
    if (activeController && activeController !== this) {
      this.remove();
      return;
    }
    activeController = this;

    this.#content = this.querySelector('[data-modal-content]');
    this.#dialog = this.querySelector('.ts-quick-view__dialog');
    this.#dialogComponent = /** @type {QuickViewDialogElement | null} */ (this.querySelector('dialog-component'));
    this.#loadingOverlay = this.querySelector('[data-loading-overlay]');

    if (!this.#content || !this.#dialogComponent) return;

    // Relocate the dialog to <main id="MainContent">: it carries
    // `data-template="product.ts-pdp-standard"`, which almost every TS PDP style
    // is scoped under, and it escapes any carousel/section nesting that would
    // make the injected gallery slideshow self-disable.
    const main = document.querySelector('main#MainContent') || document.body;
    main.append(this.#dialogComponent);
    this.#dialogComponent.dataset.template = `product.${PDP_VIEW}`;

    // Delegated, CAPTURE phase: a product card binds its own click handler
    // (view-transition navigation) closer to the target, and cards carry an
    // overlay link — capturing lets us preventDefault + stopPropagation before
    // either runs.
    document.addEventListener('click', this.#onClick, true);

    // A trigger can sit inside a Horizon slideshow (essential features), whose
    // drag handling treats any >=1px mouse travel as a drag and takes pointer
    // capture — retargeting the resulting click to the slideshow, so #onClick
    // never sees the trigger. Track the press point to recover those clicks
    // (same pattern as ts-searchspring-collection's promoted filters).
    document.addEventListener('pointerdown', this.#onPointerDown, true);

    // A variant swap inside the modal replaces <media-gallery>, wiping the
    // gallery tweaks; re-apply on the next tick. The morph can also wipe the
    // Kiwi size-guide render — re-init only then: the product hasn't changed,
    // so a surviving rendered anchor is still valid, and skipping avoids a
    // redundant Kiwi pass + link flash per click.
    this.#dialog?.addEventListener('variant:update', () => {
      requestAnimationFrame(() => {
        if (this.#content) {
          this.#applyGalleryTweaks(this.#content, this.#currentUrl);
          if (!isKiwiHydrated(this.#content)) initKiwiSizeGuide(this.#content);
          this.#initTrueFit(this.#content);
          this.#repaintVariantOptions();
        }
      });
    });

    // iOS WebKit stops repainting text-decoration-color inside the dialog's
    // scroller after a programmatic scroll (TrueFit/Kiwi hydration, validation
    // message), so the selected-size underline silently never paints — kick a
    // fresh paint on every pick (and post-morph above, since morph reuses nodes).
    this.#dialog?.addEventListener('change', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target?.closest?.('.variant-option')) this.#repaintVariantOptions();
    });

    // Colorway swatches in the injected PDP are links to sibling
    // color-product PDPs (see ts-color-swatches) — swap the modal content to
    // that colorway in place instead of letting the page navigate.
    this.#dialog?.addEventListener('click', this.#onSwatchClick);

    // Registered after the swatch handler (same-node listener order) so its
    // cancelled swatch clicks are skipped via defaultPrevented.
    this.#dialog?.addEventListener('click', this.#onNavigateAway);

    // Live modality tracking while open (see #openDialog): keydown = keyboard
    // (restore the close button's focus ring a pointer open suppressed),
    // pointerdown = pointer. Same pattern as ts-look-card.js / cart-drawer.js.
    this.#dialog?.addEventListener('keydown', () => this.#dialog?.removeAttribute('data-pointer-open'));
    this.#dialog?.addEventListener('pointerdown', () => this.#dialog?.setAttribute('data-pointer-open', 'true'));
  }

  disconnectedCallback() {
    if (activeController === this) {
      document.removeEventListener('click', this.#onClick, true);
      document.removeEventListener('pointerdown', this.#onPointerDown, true);
      activeController = null;
    }
  }

  /**
   * Open the Quick View for a product URL directly, without a
   * `.ts-quick-view-trigger` click. Public API: the style-guide pages' DCatalog
   * custom Liquid (templates/page.*-style-guide.json) opens products through it.
   *
   * @param {string} url - Absolute or relative product URL.
   * @param {{ trigger?: HTMLElement | null }} [options] - `trigger` receives
   *   focus back when the modal closes (`showModal()` restores focus to it).
   */
  openForUrl(url, { trigger = null } = {}) {
    if (!url) return;
    this.#lastTrigger = trigger;
    // Not a pointer click, so let focus return to the trigger on close.
    this.#openedByPointer = false;
    this.#open(url);
  }

  /** @type {{ x: number, y: number } | null} Pointer press point, for swallowed-click recovery. */
  #pressPoint = null;

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pressPoint = { x: event.clientX, y: event.clientY };
  };

  /**
   * Recover a trigger click swallowed by a hosting slideshow: once its drag
   * handling takes pointer capture, the click retargets to the slideshow
   * element instead of the trigger. If the pointer barely travelled (below the
   * slideshow's own 10px swipe threshold), treat it as a click on whatever
   * trigger sits under the release point. Real drags travel further and keyboard
   * activations (detail 0) target the trigger directly, so both fall through.
   * @param {MouseEvent} event
   * @returns {Element | null}
   */
  #recoverSwallowedTrigger(event) {
    const pressPoint = this.#pressPoint;
    this.#pressPoint = null;

    // Only clicks a slideshow actually retargeted (capture makes the
    // slideshow element itself the click target) — an ordinary press/release
    // target mismatch elsewhere on the page must not open the Quick View.
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('slideshow-component')) return null;

    if (!event.detail || !pressPoint) return null;
    if (Math.hypot(event.clientX - pressPoint.x, event.clientY - pressPoint.y) > 8) return null;

    return document.elementFromPoint(event.clientX, event.clientY)?.closest('.ts-quick-view-trigger') ?? null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;

    const trigger = target.closest('.ts-quick-view-trigger') ?? this.#recoverSwallowedTrigger(event);
    if (!trigger) return;

    const url = this.#resolveProductUrl(trigger);
    if (!url) return;

    // The trigger sits over the card's overlay link; stop the navigation.
    event.preventDefault();
    event.stopPropagation();
    // `detail > 0` is a pointer click; keyboard Enter/Space synthesize `detail: 0`.
    this.#openedByPointer = /** @type {MouseEvent} */ (event).detail > 0;
    this.#lastTrigger = /** @type {HTMLElement} */ (trigger);
    this.#open(url);
  };

  /**
   * `showModal()` snapshots the focused element and `dialog.close()` restores
   * focus to it — flagged `:focus-visible`, so a pointer open ends with a
   * stray focus ring: around the trigger's card on desktop (`:focus-within`
   * keeps its hover reveals on), or around whatever focusable ancestor the
   * tap focused on iOS, where taps skip buttons (e.g. a hosting slideshow's
   * `tabindex="-1"` scroller). Blurring AFTER close would flash the ring for
   * a frame — instead drop focus before `showModal()` so there is nothing to
   * restore. Keyboard opens keep their focus (they want it back on close).
   */
  #dropPointerFocus() {
    if (!this.#openedByPointer) return;
    const trigger = this.#lastTrigger;
    const active = document.activeElement;
    if (!trigger || !(active instanceof HTMLElement) || active === document.body) return;
    if (active === trigger || active.contains(trigger)) active.blur();
  }

  /**
   * Intercept colorway-swatch link clicks inside the dialog and load that
   * color-product into the modal instead of navigating to its PDP.
   * @param {Event} event
   */
  #onSwatchClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;

    const swatch = target.closest('.ts-color-swatches a[href]');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const href = swatch.getAttribute('href');
    // Re-selecting the current colorway is a no-op (still no navigation).
    if (!href || swatch.getAttribute('aria-current') === 'true') return;
    this.#open(href);
  };

  /**
   * A plain same-tab link navigation from inside the open dialog ("View Full
   * Details", a fit-selector link) must release the body scroll lock before it
   * commits: the lock zeroes window.scrollY, so the browser would snapshot 0
   * as this history entry's scroll offset and back-navigating to the list
   * would land at the top instead of the saved position. dialog.close() queues
   * the `close` event task, whose lock release (#releaseBodyLock in dialog.js)
   * restores the real offset long before the navigation can commit — a commit
   * needs at least a network round-trip. (closeDialog() is unusable here: it
   * awaits the closing animation.)
   * @param {MouseEvent} event
   */
  #onNavigateAway = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;
    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    // _top/_parent navigate this tab too (the page is not framed).
    if ((link.target && !['_self', '_top', '_parent'].includes(link.target)) || link.hasAttribute('download')) return;

    const href = link.getAttribute('href') || '';
    if (href.startsWith('#') || href.startsWith('javascript:')) return;

    if (this.#dialog instanceof HTMLDialogElement && this.#dialog.open) this.#dialog.close();
  };

  /**
   * Resolve the product URL for a trigger from the nearest card's link. Works
   * for the native `ts-product-card` and the Searchspring `ts-plp-card` (whose
   * links retarget to the selected colorway).
   * @param {Element} trigger
   * @returns {string}
   */
  #resolveProductUrl(trigger) {
    const card = trigger.closest('[data-ss-card], .ts-product-card, product-card');
    if (!card) return '';
    const link = card.querySelector(
      '[data-ss-overlay-link], [data-ss-link], .product-card__link, a[href]:not(.ts-quick-view-trigger)'
    );
    return link?.getAttribute('href') || '';
  }

  /**
   * Strip `?variant=` from a product URL. The modal must open with nothing
   * chosen (see `ts-variant-requires-selection`), but PLP card links carry the
   * variant from the Searchspring feed's `url` field — and the server reads
   * that as an explicit selection, rendering every option preselected and
   * dropping the "select a size" message. Normalizing here keeps the fetch, the
   * cache key, and the "View Full Details" link in agreement.
   * @param {string} url - Absolute or relative product URL.
   * @returns {string} The URL without a variant param.
   */
  #stripVariantParam(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('variant');
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  /** @param {string} url */
  async #open(url) {
    url = this.#stripVariantParam(url);
    this.#currentUrl = url;

    // A shown section is never re-inserted. Removing and re-adding it would
    // reconnect its custom elements, and Horizon's product-form (media,
    // price-per-item, sticky ATC too) registers its listeners with a one-shot
    // AbortController that disconnect aborts — the reconnected form would
    // never hear another variant update and keep submitting the last variant
    // id it saw. Re-opening the mounted product leaves it untouched; any other
    // cached product gets a fresh clone of its snapshot.
    const cached = this.#sections.get(url);
    if (cached) {
      this.#openDialog();
      if (this.#injectedUrl === url && this.#content?.firstElementChild) return;
      await new Promise(requestAnimationFrame);
      this.#inject(/** @type {Element} */ (cached.cloneNode(true)), url);
      return;
    }

    if (this.#loading) return;
    this.#loading = true;
    this.#showOverlay();

    try {
      // The TS PDP composition is the default product template, so the plain
      // product URL already renders it — no `?view=` needed. (PDP_VIEW still
      // stamps the dialog wrapper's data-template for scoped styling.)
      const fetchUrl = new URL(url, window.location.origin);
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.product-information');
      if (!section) {
        console.error('[ts-quick-view] could not locate .product-information in response');
        return;
      }
      if (!this.#content) return;

      // Shopify subsets compiled CSS/JS bundles per page, so on non-PDP hosts
      // the injected markup's styles/components are missing — adopt the fetched
      // page's assets before revealing the content.
      await this.#adoptAssets(doc);

      this.#sections.set(url, /** @type {Element} */ (section.cloneNode(true)));

      // Open BEFORE injecting so the gallery slideshow measures the real
      // viewport instead of a 0×0 `display: none` dialog.
      this.#openDialog();
      await new Promise(requestAnimationFrame);
      this.#inject(section, url);
    } catch (error) {
      console.error('[ts-quick-view] failed to load product:', error);
    } finally {
      this.#hideOverlay();
      this.#loading = false;
    }
  }

  /**
   * Inject a `.product-information` element into the open dialog and hydrate
   * the third-party widgets it carries.
   * @param {Element} section
   * @param {string} url
   */
  #inject(section, url) {
    if (!this.#content) return;
    this.#applyGalleryTweaks(section, url);
    this.#content.replaceChildren(section);
    this.#injectedUrl = url;
    this.#applyColorScheme(section);
    this.#initOkendoWidgets();
    initKiwiSizeGuide(this.#content);
    this.#initTrueFit(this.#content);
  }

  /**
   * Adopt the fetched page's stylesheet links and module scripts that this page
   * doesn't already have, then wait (briefly) for the new styles to load so the
   * modal doesn't flash unstyled content.
   * @param {Document} doc
   */
  async #adoptAssets(doc) {
    const existingLinks = new Set(
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(
        (el) => /** @type {HTMLLinkElement} */ (el).href
      )
    );
    /** @type {Promise<void>[]} */
    const pending = [];

    for (const el of Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))) {
      const href = /** @type {HTMLLinkElement} */ (el).href;
      if (!href || existingLinks.has(href)) continue;
      existingLinks.add(href);

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      pending.push(
        new Promise((resolve) => {
          link.onload = link.onerror = () => resolve(undefined);
        })
      );
      document.head.append(link);
    }

    const existingScripts = new Set(
      Array.from(document.querySelectorAll('script[src]')).map((el) => /** @type {HTMLScriptElement} */ (el).src)
    );

    // Module scripts carry the components the injected markup needs (Horizon's
    // and the TS blocks'); a module URL only ever executes once, so adopting by
    // src is safe. The `compiled_assets` bundles are deliberately NOT adopted:
    // they share one theme-wide URL whose chunks are gated on the host page's
    // own data-blocks/data-sections tag (looked up by id), so injected content
    // must not rely on `{% javascript %}` — use module assets instead.
    for (const el of Array.from(doc.querySelectorAll('script[src][type="module"]'))) {
      const src = /** @type {HTMLScriptElement} */ (el).src;
      if (!src || existingScripts.has(src)) continue;
      existingScripts.add(src);

      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      document.head.append(script);
    }

    // Don't block the modal forever on a slow stylesheet.
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
    await Promise.race([Promise.all(pending), timeout]);
  }

  /**
   * Copy the section's color-scheme class onto the dialog so its chrome uses the
   * same scheme as the hosted product. Swap on each product change.
   * @param {Element} section
   */
  #applyColorScheme(section) {
    if (!this.#dialog) return;
    for (const c of Array.from(this.#dialog.classList)) {
      if (c.startsWith('color-')) this.#dialog.classList.remove(c);
    }
    const colorSchemeClass = Array.from(section.classList).find((c) => c.startsWith('color-'));
    if (colorSchemeClass) this.#dialog.classList.add(colorSchemeClass);
  }

  /**
   * Configure the injected gallery for the Quick View layout (single-slide
   * carousel with prev/next arrows; the thumbnail strip stays available for
   * multi-image products) and append the "View Full Details" CTA.
   * @param {ParentNode} root
   * @param {string} url
   */
  #applyGalleryTweaks(root, url) {
    // Ensure the arrows are enabled; the QV stylesheet handles their placement.
    const slideshow = root.querySelector('slideshow-component, slideshow-container');
    if (slideshow) slideshow.classList.add('ts-quick-view__gallery');

    const productDetails = root.querySelector('.product-details');
    const detailsHost = productDetails?.querySelector(':scope > .group-block') ?? productDetails;
    if (detailsHost && !detailsHost.querySelector('.ts-quick-view__view-details')) {
      const link = document.createElement('a');
      link.className = 'ts-cta ts-cta--medium ts-quick-view__view-details';
      link.href = url;
      link.textContent = this.dataset.viewDetailsLabel || '';
      detailsHost.append(link);
    }
  }

  /**
   * Hydrate the injected Okendo widgets (e.g. the star rating). Okendo's global
   * app-embed only auto-scans its `[data-oke-*]` mounts on the initial page
   * load, so the mount we inject after load stays an empty placeholder until we
   * call its public API. `okeWidgetApi` loads asynchronously, so wait briefly
   * for it if it isn't ready yet.
   */
  #initOkendoWidgets() {
    const content = this.#content;
    if (!content) return;
    // Skip mounts Okendo has already populated.
    const widgets = Array.from(content.querySelectorAll('[data-oke-star-rating], [data-oke-widget]')).filter(
      (el) => el.childElementCount === 0
    );
    if (!widgets.length) return;

    const run = () => {
      const api = /** @type {any} */ (window).okeWidgetApi;
      if (!api?.initWidget) return false;
      for (const widget of widgets) {
        try {
          api.initWidget(widget);
        } catch (error) {
          console.error('[ts-quick-view] Okendo initWidget failed:', error);
        }
      }
      return true;
    };

    if (run()) return;
    let waited = 0;
    const timer = setInterval(() => {
      if (run() || (waited += 100) >= 5000) clearInterval(timer);
    }, 100);
  }

  /**
   * Hydrate the injected True Fit "What's My Size" placeholder
   * (`.tfc-fitrec-product`, server-rendered by `blocks/ts-true-fit.liquid`).
   * True Fit's library only scans for placeholders once at page load, so a
   * placeholder injected after that stays empty until we request a re-scan
   * via `tfcapi('calculate')`. The stub queues calls until the library loads,
   * so no readiness wait is needed. An already-hydrated widget (variant
   * updates morph around it) is left alone.
   * @param {HTMLElement} content
   */
  #initTrueFit(content) {
    // The fit modal the widget opens is a <body>-level overlay that a
    // showModal() dialog would otherwise paint over — install the top-layer
    // guard before any re-scan.
    watchThirdPartyModals();
    const placeholder = content.querySelector('.tfc-fitrec-product');
    if (!placeholder || placeholder.childElementCount > 0) return;
    const tfcapi = /** @type {any} */ (window).tfcapi;
    if (typeof tfcapi !== 'function') return;
    try {
      tfcapi('calculate');
    } catch (error) {
      console.error('[ts-quick-view] True Fit re-scan failed:', error);
    }
  }

  /**
   * Force a repaint of the variant option rows: promoting them to their own
   * compositing layer for a frame makes WebKit paint them fresh, flushing the
   * stale text-decoration paint (see the change listener in connectedCallback).
   */
  #repaintVariantOptions() {
    if (!this.#content) return;
    for (const fieldset of this.#content.querySelectorAll('.variant-option')) {
      if (!(fieldset instanceof HTMLElement)) continue;
      fieldset.style.transform = 'translateZ(0)';
      requestAnimationFrame(() => fieldset.style.removeProperty('transform'));
    }
  }

  #showOverlay() {
    if (this.#loadingOverlay) this.#loadingOverlay.hidden = false;
  }

  #hideOverlay() {
    if (this.#loadingOverlay) this.#loadingOverlay.hidden = true;
  }

  #openDialog() {
    // Pointer opens: showModal()'s programmatic focus lands on the close
    // button and browsers flag it :focus-visible — data-pointer-open lets the
    // modal CSS suppress that ring; keyboard opens keep it. On already-open
    // swaps (colorway) the live modality listeners own the attribute.
    if (this.#dialog instanceof HTMLDialogElement && !this.#dialog.open) {
      this.#dialog.toggleAttribute('data-pointer-open', this.#openedByPointer);
      this.#dropPointerFocus();
    }
    this.#dialogComponent?.showDialog?.();
  }
}

if (!customElements.get('ts-quick-view')) {
  customElements.define('ts-quick-view', TsQuickView);
}
